import React, { useState, useEffect, useMemo } from "react";
import SmartButton from "../buttons/SmartButton";
import DateInput from "../forms/DateInput";
import FilterModal from "../popup/FilterModal";
import { getLanguageContent } from "../../../utils/language/languageSelector";
import { FILTER_SCHEMA } from "../../../utils/schemas/language/components/filterSchema";

const Filter = ({
  filters = {},
  filterTitle,
  applyText,
  onFilterChange,
  visibleFilters = ["date", "service", "status", "category"],
  statusOptions,
}) => {
  const content = getLanguageContent(FILTER_SCHEMA);
  const resolvedFilterTitle = filterTitle ?? content.filter_by;
  const resolvedApplyText = applyText ?? content.apply;
  const resolvedStatusOptions = useMemo(
    () =>
      statusOptions ?? [
        { value: "", label: content.all_status },
        { value: "Completed", label: content.status.completed },
        { value: "Pending", label: content.status.pending },
        { value: "Canceled", label: content.status.canceled },
      ],
    [content, statusOptions],
  );
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [appliedFilters, setAppliedFilters] = useState(filters);

  // Sync internal state with props when filters change
  useEffect(() => {
    setLocalFilters(filters);
    setAppliedFilters(filters);
  }, [filters]);

  const handleMobileFilterClick = () => {
    setIsMobileFilterOpen(true);
  };

  const handleCloseMobileFilter = () => {
    setIsMobileFilterOpen(false);
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...localFilters, [field]: value };
    setLocalFilters(newFilters);
  };

  const handleApplyFilter = () => {
    setAppliedFilters(localFilters);
    onFilterChange(localFilters);
    setIsMobileFilterOpen(false);
  };

  const handleRemoveFilter = (filterKey) => {
    const newFilters = { ...appliedFilters, [filterKey]: "" };
    setAppliedFilters(newFilters);
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  const hasActiveFilters = () => {
    return Object.values(appliedFilters).some((value) => value !== "");
  };

  const getFilterDisplayValue = (key, value) => {
    switch (key) {
      case "date":
        // Parse date string directly to avoid timezone issues (format: YYYY-MM-DD)
        const [year, month, day] = value.split("-");
        const dateObj = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
        return dateObj.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
          year: "numeric",
        });
      case "service":
      case "status":
      case "category":
        return value;
      default:
        return value;
    }
  };

  return (
    <div className="filter-box">
      <div className="filter-container">
        <div className="filter-content">
          <div className="filter-label">
            <span>{resolvedFilterTitle}</span>
          </div>
          <div className="filter-controls">
            {visibleFilters.includes("date") && (
              <div className="filter-field">
                <DateInput
                  value={localFilters.date}
                  onChange={(e) => handleFilterChange("date", e.target.value)}
                />
              </div>
            )}
            {visibleFilters.includes("service") && (
              <div className="filter-field">
                <select
                  className="form-input"
                  value={localFilters.service}
                  onChange={(e) =>
                    handleFilterChange("service", e.target.value)
                  }
                >
                  <option value="">{content.all_services}</option>
                  <option value="Tax Filing">{content.tax_filing}</option>
                </select>
              </div>
            )}
            {visibleFilters.includes("status") && (
              <div className="filter-field">
                <select
                  className="form-input"
                  value={localFilters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  {resolvedStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {visibleFilters.includes("category") && (
              <div className="filter-field">
                <select
                  className="form-input"
                  value={localFilters.category}
                  onChange={(e) =>
                    handleFilterChange("category", e.target.value)
                  }
                >
                  {resolvedStatusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="filter-actions">
              <SmartButton
                type="button"
                className="cta-button primary"
                text={resolvedApplyText}
                onClick={handleApplyFilter}
              />
            </div>
          </div>
          <div className="filter-mobile-trigger">
            <div
              style={{
                display: "flex",
                padding: "6px",
                background: "#EBF5FF",
                borderRadius: "6px",
                alignItems: "center",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/icons/filter.svg`}
                alt={content.filter_alt}
                onClick={handleMobileFilterClick}
                style={{ cursor: "pointer" }}
              />
            </div>
          </div>
        </div>

        {/* Mobile Filter Modal */}
        <FilterModal
          isOpen={isMobileFilterOpen}
          onClose={handleCloseMobileFilter}
          onApply={handleApplyFilter}
          title={resolvedFilterTitle}
          applyText={resolvedApplyText}
          filters={localFilters}
          onFilterChange={handleFilterChange}
          visibleFilters={visibleFilters}
          statusOptions={statusOptions}
        >
          {visibleFilters.includes("category") && (
            <div className="filter-field">
              <select
                className="form-input"
                value={localFilters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
              >
                {resolvedStatusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </FilterModal>
      </div>

      {/* Filter Results */}
      {hasActiveFilters() && (
        <div className="filter-results">
          <div className="filter-results-content">
            {Object.entries(appliedFilters).map(([key, value]) => {
              if (!value) return null;
              return (
                <div key={key} className="filter-chip">
                  <span className="filter-chip-text">
                    {getFilterDisplayValue(key, value)}
                  </span>
                  <button
                    className="filter-chip-remove"
                    onClick={() => handleRemoveFilter(key)}
                    title={content.remove_filter.replace("{key}", key)}
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default Filter;
